import { pageTitle } from '@palaccount/config';
import {
  CANDIDATE_STAGES,
  REJECTION_REASONS,
  type CandidateResponse,
  type RequisitionResponse,
} from '@palaccount/contracts';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount, Ltr } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Gated, GatedGroup } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useBaseCurrency, useCompanyToday } from '@/lib/session';
import { cn } from '@/lib/utils';

import {
  useAddScorecard,
  useAdvanceCandidate,
  useCandidates,
  useCandidatesHeld,
  useCreateCandidate,
  useCreateRequisition,
  useHireCandidate,
  useRejectCandidate,
  useRequisitionOptions,
  useRequisitions,
  useSaveOffer,
  useScheduleInterview,
  useSetTalentPool,
  useTransitionRequisition,
} from './api';
import { HrTabs } from './hr-tabs';

/**
 * HR-05 — Recruitment: the six-column pipeline, the candidate detail with
 * scorecards / offer / hire, and the requisition cards whose Approve and
 * Open answer to hr.approve (BR-HR-05-15). Hiring creates a REAL employee
 * (BR-HR-05-05/-06) — the confirmation names the EMP code.
 *
 * ERR-HR-05-06 IS NOW ANSWERED — and the note it replaces is worth keeping in
 * outline, because the shape recurs. It was HALF BUILT: the server had
 * POST /api/hr/candidates/:id/talent-pool and enforced BR-HR-05-17 on it (it
 * refuses a candidate who is not rejected), but no hook in `./api.ts` called
 * that route, and the only keep-on-file checkbox lived inside the reject
 * panel — drawn for ACTIVE candidates only. So the row's own advice, "set it
 * on the candidate", could not be followed from the interface at all.
 *
 * A working route with nothing calling it is the least visible kind of gap:
 * every server test passes, the contract is complete, and the capability does
 * not exist for any user. `useSetTalentPool` and the checkbox in
 * `CandidateDetail` below are the missing half.
 *
 * ═══ ONE HR-05 §14 ROW THIS SCREEN DOES **NOT** ANSWER — NOW RULED ═══════
 * Recorded here with the scanner's ignore mark on the id's own line, so that
 * saying this does not quietly count as citing it.
 *
 *   ERR-HR-05-07 — rule-coverage:ignore — RETIRED by delegated ruling D48
 *     (27 Aug 2026), reversibly; the spec carries the retirement notice at
 *     the row itself. The row names a stage-filter control ("none is drawn,
 *     although one is fully wired"). This build draws no such control and
 *     does not intend to: the prototype's single flat candidate list is
 *     superseded by the pipeline board, whose six columns each send their own
 *     stage as a SERVER filter — there is no flat list for a global filter
 *     to narrow, and drawing one would duplicate the columns. Citing a
 *     message that names a control the build does not have would move the
 *     coverage counter while making it less true; D48 turned that standing
 *     disposition into the document's own text. The row returns if a flat
 *     candidates view is ever built.
 */
export function RecruitmentPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'pipeline' | 'requisitions'>('pipeline');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('hr.recruitmentTitle'));
  }, [t]);

  return (
    <div className="grid gap-4">
      <HrTabs />
      <div className="flex flex-wrap gap-2">
        {(['pipeline', 'requisitions'] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm',
              tab === key ? 'bg-accent font-medium' : 'text-muted-foreground hover:bg-accent/50',
            )}
          >
            {t(`hr.recTab.${key}`)}
          </button>
        ))}
      </div>
      {notice ? <p className="rounded-md bg-primary/10 p-3 text-sm">{notice}</p> : null}
      {error ? (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {tab === 'pipeline' ? (
        <PipelineTab onNotice={setNotice} onError={setError} />
      ) : (
        <RequisitionsTab onError={setError} />
      )}
    </div>
  );
}

/** Each of §7's six columns asks for one block at a time (NFR-42). */
const PIPELINE_PAGE_SIZE = 25;
/** The Requisitions tab is a card grid, three to a row on a wide screen. */
const REQUISITIONS_PAGE_SIZE = 24;

/**
 * ONE STAGE COLUMN of HR-05's board: its own paged read of the same route,
 * carrying only its own stage.
 *
 * A component rather than a `.map()` body because each column is its own
 * query and its own page, and a hook cannot be called inside a loop — the
 * shape CRM-01's and CRM-02's boards both take. The header's count is the
 * SERVER's total for the stage, so "Rejected 412" over twenty-five cards
 * reads as a page rather than as a contradiction.
 */
function StageColumn({
  stage,
  selectedId,
  onSelect,
}: {
  stage: CandidateResponse['stage'];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  const paging = usePagedState(PIPELINE_PAGE_SIZE, `stage-${stage}`);
  const column = useCandidates({ stage }, paging.page, PIPELINE_PAGE_SIZE);
  const range = usePageRange(column.data, PIPELINE_PAGE_SIZE);

  return (
    <div className="grid content-start gap-2 rounded-md bg-muted/40 p-2">
      <div className="flex items-center justify-between px-1 text-xs font-semibold">
        <span>{t(`hr.stage.${stage}`)}</span>
        {/* While the read is in flight the count is unknown — an ellipsis,
            not a 0 that would read as "nobody in this stage". */}
        <span className="text-muted-foreground">{column.isPending ? '…' : range.total}</span>
      </div>
      {column.isPending ? (
        /*
         * NFR-43 — a column whose read is in flight shows working cards in
         * the shape of the cards it is fetching. Before this branch the
         * column drew header-plus-nothing: six stages that read as empty
         * while every one of them was still loading.
         * `nfr43-loading-states.test.tsx` parks the reads and holds this.
         */
        <div className="grid gap-2" aria-busy>
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-md border border-border bg-card" />
          ))}
        </div>
      ) : null}
      {(column.data?.candidates ?? []).map((candidate) => (
        <button
          key={candidate.id}
          type="button"
          onClick={() => onSelect(candidate.id)}
          className={cn(
            'grid gap-0.5 rounded-md border border-border bg-card p-2 text-start text-sm hover:border-primary',
            selectedId === candidate.id && 'border-primary',
          )}
        >
          <span className="font-medium">{candidate.name}</span>
          <span className="truncate text-xs text-muted-foreground">
            {candidate.requisitionTitle}
          </span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            {t(`hr.source.${candidate.source}`)}
            {candidate.averageRating !== null ? <span>★ {candidate.averageRating}</span> : null}
            {candidate.talentPool ? (
              <span className="rounded-full bg-blue-500/10 px-1.5 text-blue-700 dark:text-blue-400">
                {t('hr.talentPool')}
              </span>
            ) : null}
          </span>
        </button>
      ))}
      {range.pages > 1 ? (
        <Pager
          page={paging.page}
          pages={range.pages}
          first={range.first}
          last={range.last}
          total={range.total}
          onPage={paging.setPage}
          label={t(`hr.stage.${stage}`)}
        />
      ) : null}
    </div>
  );
}

function PipelineTab({
  onNotice,
  onError,
}: {
  onNotice: (message: string) => void;
  onError: (message: string | null) => void;
}) {
  const { t } = useTranslation();
  // The "Applied for" `<select>` is a PICKER, not a list — see the hook.
  const { data: requisitions } = useRequisitionOptions();
  /*
   * NFR-42 — the board is one paged read PER COLUMN, so the row the detail
   * card shows is held here rather than looked up in an array of everybody.
   * `selectedId` alone would have needed the whole pipeline in memory, which
   * is the thing the columns stopped fetching.
   */
  const [adding, setAdding] = useState(false);

  /*
   * The SELECTED candidate, as its own bounded read by ID rather than a
   * lookup in an array of everybody. It is what lets §7's board be one paged
   * read per column: the detail card keeps its row when a Move sends that row
   * to a different column, and when the column it came from is on page 2.
   */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const held = useCandidatesHeld(selectedId === null ? [] : [selectedId]);
  const selected = held.data?.[0] ?? null;

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{t('hr.pipelineHint')}</p>
        <Gated permission="hr.create" reasonKey="perm.cannot.createRequisitions">
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus aria-hidden /> {t('hr.addCandidate')}
          </Button>
        </Gated>
      </div>

      {adding ? (
        <NewCandidateCard
          requisitions={requisitions ?? []}
          onCancel={() => setAdding(false)}
          onError={onError}
          onCreated={(id) => {
            setAdding(false);
            setSelectedId(id);
          }}
        />
      ) : null}

      {/* The six-column board (§4) — one paged read PER COLUMN (NFR-42), each
          carrying its own stage as a SERVER filter. */}
      <div className="grid gap-2 overflow-x-auto md:grid-cols-3 xl:grid-cols-6">
        {CANDIDATE_STAGES.map((stage) => (
          <StageColumn
            key={stage}
            stage={stage}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        ))}
      </div>

      {/*
        ERR-HR-05-04 / BR-HR-05-14 — the detail renders HERE, under the board,
        rather than on a tab of its own. The prototype put the board and the
        detail panel on different tabs, so clicking a card selected a
        candidate and produced no visible effect at all; the §14 row's advice
        was to go and switch tabs. There is no Candidates tab now, which is
        why nothing has to switch.
      */}
      {selected ? (
        <CandidateDetail
          candidate={selected}
          onNotice={onNotice}
          onError={onError}
        />
      ) : null}
    </div>
  );
}

function NewCandidateCard({
  requisitions,
  onCancel,
  onCreated,
  onError,
}: {
  requisitions: RequisitionResponse[];
  onCancel: () => void;
  onCreated: (id: string) => void;
  onError: (message: string | null) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateCandidate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [requisitionId, setRequisitionId] = useState('');

  const complete = name.trim() !== '' && email.trim() !== '' && requisitionId !== '';

  return (
    <Card>
      <CardContent className="grid gap-3">
        <h3 className="text-sm font-semibold">{t('hr.addCandidate')}</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1">
            <Label htmlFor="cand-name">{t('hr.fullName')}</Label>
            <Input id="cand-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="cand-email">{t('hr.email')}</Label>
            <Input id="cand-email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="cand-phone">{t('hr.phone')}</Label>
            <Input id="cand-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="cand-req">{t('hr.appliedFor')}</Label>
            <NativeSelect id="cand-req" value={requisitionId} onChange={(e) => setRequisitionId(e.target.value)}>
              <option value="">—</option>
              {requisitions.map((requisition) => (
                <option key={requisition.id} value={requisition.id}>
                  {requisition.jobTitle}
                </option>
              ))}
            </NativeSelect>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            disabled={!complete}
            onClick={() => {
              onError(null);
              create.mutate(
                { name, email, requisitionId, ...(phone ? { phone } : {}) },
                {
                  onSuccess: (candidate) => onCreated(candidate.id),
                  onError: (cause) => onError(cause instanceof Error ? cause.message : t('common.saveFailed')),
                },
              );
            }}
          >
            {t('common.save')}
          </Button>
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CandidateDetail({
  candidate,
  onNotice,
  onError,
}: {
  candidate: CandidateResponse;
  onNotice: (message: string) => void;
  onError: (message: string | null) => void;
}) {
  const { t } = useTranslation();
  const advance = useAdvanceCandidate();
  const reject = useRejectCandidate();
  const hire = useHireCandidate();
  const interview = useScheduleInterview();
  const scorecard = useAddScorecard();
  const saveOffer = useSaveOffer();

  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [talentPool, setTalentPool] = useState(false);
  const [round, setRound] = useState<'phone_screen' | 'technical' | 'panel' | 'final'>('technical');
  const [rating, setRating] = useState(0);
  const [offerSalary, setOfferSalary] = useState(candidate.offerSalary ?? '');
  // G-09: an offer not yet dated starts as the company's today.
  const today = useCompanyToday();
  const [offerStart, setOfferStart] = useState(candidate.offerStartDate ?? today);
  const [offerStatus, setOfferStatus] = useState(candidate.offerStatus ?? 'sent');

  const setKeepOnFile = useSetTalentPool();

  const active = ['applied', 'screening', 'interview', 'offer'].includes(candidate.stage);
  const onErrorCb = (cause: unknown) =>
    onError(cause instanceof Error ? cause.message : t('common.saveFailed'));

  return (
    <Card>
      <CardContent className="grid gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="me-auto font-semibold">{candidate.name}</h3>
          <span className="text-sm text-muted-foreground">{candidate.email}</span>
          {candidate.averageRating !== null ? (
            <span className="text-sm">★ {candidate.averageRating}</span>
          ) : null}
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{t(`hr.stage.${candidate.stage}`)}</span>
        </div>

        {/* A closed requisition's pipeline is finished for everybody. */}
        {active ? (
          <GatedGroup permission="hr.edit" reasonKey="perm.cannot.moveCandidates">
          <div className="flex flex-wrap gap-2">
            {candidate.stage !== 'offer' ? (
              <Button size="sm" onClick={() => advance.mutate(candidate.id, { onError: onErrorCb })}>
                {t('hr.advance')}
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() =>
                  hire.mutate(candidate.id, {
                    onSuccess: (body) => onNotice(body.message),
                    onError: onErrorCb,
                  })
                }
              >
                {t('hr.convertToEmployee')}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                interview.mutate(candidate.id, {
                  onSuccess: (body) => onNotice(body.message),
                  onError: onErrorCb,
                })
              }
            >
              {t('hr.scheduleInterview')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setRejecting(true)}>
              {t('hr.reject')}
            </Button>
          </div>
          </GatedGroup>
        ) : null}

        {/*
          ERR-HR-05-06 — the keep-on-file decision, ON THE CANDIDATE RECORD.
          The row's advice is "set it on the candidate", and until this control
          existed that could not be followed: the only other switch lives in
          the reject panel above, which is drawn for ACTIVE candidates, so the
          choice became unreachable at the very moment it started to matter.

          Drawn for a REJECTED candidate only, which is the same condition the
          server enforces (it refuses the route otherwise). The state is read
          straight from `candidate.talentPool` rather than held in a local
          `useState`, so the checkbox shows what the record says instead of
          what this component last remembered — the reject panel's own
          `talentPool` state is a draft for an unsent rejection and means
          nothing here.
        */}
        {candidate.stage === 'rejected' ? (
          <GatedGroup permission="hr.edit" reasonKey="perm.cannot.moveCandidates">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={candidate.talentPool}
                onCheckedChange={(checked) =>
                  setKeepOnFile.mutate(
                    { candidateId: candidate.id, talentPool: checked === true },
                    { onError: onErrorCb },
                  )
                }
              />
              {t('hr.keepOnFile')}
            </label>
          </GatedGroup>
        ) : null}

        {rejecting ? (
          <div className="grid gap-2 rounded-md border border-destructive/40 p-3">
            <Label htmlFor="rej-reason">{t('hr.rejectionReasonLabel')}</Label>
            <NativeSelect
              id="rej-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="h-10 w-64"
            >
              <option value="">—</option>
              {REJECTION_REASONS.map((key) => (
                <option key={key} value={key}>
                  {t(`hr.rejReason.${key}`)}
                </option>
              ))}
            </NativeSelect>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={talentPool} onCheckedChange={(checked) => setTalentPool(checked === true)} />
              {t('hr.keepOnFile')}
            </label>
            <div className="flex gap-2">
              {/* Greyed until a reason is chosen — the one panel the
                  prototype got right (§6). */}
              <Button
                variant="destructive"
                size="sm"
                disabled={reason === ''}
                onClick={() =>
                  reject.mutate(
                    { candidateId: candidate.id, reason: reason as never, talentPool },
                    {
                      onSuccess: () => setRejecting(false),
                      onError: onErrorCb,
                    },
                  )
                }
              >
                {t('hr.rejectCandidate')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setRejecting(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Scorecards — always present (BR-HR-05-16). */}
          <div className="grid content-start gap-2">
            <h4 className="text-sm font-semibold">{t('hr.scorecards')}</h4>
            {candidate.scorecards.map((card) => (
              <div key={card.id} className="flex items-center gap-2 rounded-md border border-border p-2 text-sm">
                <span>{t(`hr.round.${card.round}`)}</span>
                <span className="text-amber-600">{'★'.repeat(card.rating)}</span>
                {card.notes ? <span className="text-xs text-muted-foreground">{card.notes}</span> : null}
              </div>
            ))}
            <GatedGroup permission="hr.edit" reasonKey="perm.cannot.scoreCandidates">
              <div className="flex flex-wrap items-center gap-2">
                <NativeSelect
                  value={round}
                  onChange={(e) => setRound(e.target.value as typeof round)}
                  className="h-9 w-40"
                  aria-label={t('hr.round.label')}
                >
                  {(['phone_screen', 'technical', 'panel', 'final'] as const).map((key) => (
                    <option key={key} value={key}>
                      {t(`hr.round.${key}`)}
                    </option>
                  ))}
                </NativeSelect>
                <div role="radiogroup" aria-label={t('hr.rating')} className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      role="radio"
                      aria-checked={rating === star}
                      aria-label={`${star}`}
                      onClick={() => setRating(star)}
                      className={cn('px-0.5 text-lg', star <= rating ? 'text-amber-600' : 'text-muted-foreground')}
                    >
                      ★
                    </button>
                  ))}
                </div>
                {/* Disabled until a rating is chosen (defect fix). */}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={rating === 0}
                  onClick={() =>
                    scorecard.mutate(
                      { candidateId: candidate.id, round, rating },
                      { onSuccess: () => setRating(0), onError: onErrorCb },
                    )
                  }
                >
                  {t('hr.submitScorecard')}
                </Button>
              </div>
            </GatedGroup>
          </div>

          {/* The offer (§5d). */}
          <div className="grid content-start gap-2">
            <h4 className="text-sm font-semibold">{t('hr.offer')}</h4>
            <div className="grid gap-2 sm:grid-cols-3">
              <div className="grid gap-1">
                <Label htmlFor="offer-salary">{t('hr.proposedSalary')}</Label>
                <Input id="offer-salary" value={offerSalary} onChange={(e) => setOfferSalary(e.target.value)} />
              </div>
              <div className="grid gap-1">
                <Label htmlFor="offer-start">{t('hr.startDate')}</Label>
                <Input id="offer-start" type="date" value={offerStart} onChange={(e) => setOfferStart(e.target.value)} />
              </div>
              <div className="grid gap-1">
                <Label htmlFor="offer-status">{t('hr.offerStatus')}</Label>
                <NativeSelect
                  id="offer-status"
                  value={offerStatus}
                  onChange={(e) => setOfferStatus(e.target.value as typeof offerStatus)}
                >
                  {(['sent', 'accepted', 'declined', 'negotiating'] as const).map((key) => (
                    <option key={key} value={key}>
                      {t(`hr.offerState.${key}`)}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            </div>
            <Gated permission="hr.edit" reasonKey="perm.cannot.makeOffers">
              <div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!/^\d{1,13}(\.\d{1,2})?$/.test(offerSalary) || offerStart === ''}
                  onClick={() =>
                    saveOffer.mutate(
                      { candidateId: candidate.id, salary: offerSalary, startDate: offerStart, status: offerStatus },
                      { onError: onErrorCb },
                    )
                  }
                >
                  {t('hr.saveOffer')}
                </Button>
              </div>
            </Gated>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RequisitionsTab({ onError }: { onError: (message: string | null) => void }) {
  // GEN-107: what these figures are IN, from the session rather than a
  // literal. Every one of them is a ledger or stock valuation, which the
  // company holds in its base currency; nothing here converts, so GEN-116
  // keeps the disclosure off the screen.
  const baseCurrency = useBaseCurrency();
  const { t } = useTranslation();
  // NFR-42: the Requisitions tab is a browsable list of its own. The board's
  // six columns each carry their own `?…Page=`, so this one is named too.
  const reqPaging = usePagedState(REQUISITIONS_PAGE_SIZE, 'req');
  const { data: requisitionPage } = useRequisitions(reqPaging.page, REQUISITIONS_PAGE_SIZE);
  const requisitions = requisitionPage?.requisitions;
  const reqRange = usePageRange(requisitionPage, REQUISITIONS_PAGE_SIZE);
  const create = useCreateRequisition();
  const transition = useTransitionRequisition();
  const [adding, setAdding] = useState(false);
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('finance');
  const [openings, setOpenings] = useState('1');
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');

  const onErrorCb = (cause: unknown) =>
    onError(cause instanceof Error ? cause.message : t('common.saveFailed'));

  return (
    <div className="grid gap-4">
      <Gated permission="hr.create" reasonKey="perm.cannot.createCandidates">
        <div>
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus aria-hidden /> {t('hr.addRequisition')}
          </Button>
        </div>
      </Gated>

      {adding ? (
        <Card>
          <CardContent className="grid gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1">
                <Label htmlFor="req-title">{t('hr.jobTitle')}</Label>
                <Input id="req-title" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
              </div>
              <div className="grid gap-1">
                <Label htmlFor="req-dept">{t('hr.department')}</Label>
                <NativeSelect id="req-dept" value={department} onChange={(e) => setDepartment(e.target.value)}>
                  {(['finance', 'sales', 'operations', 'retail', 'hr', 'it'] as const).map((dept) => (
                    <option key={dept} value={dept}>
                      {t(`hr.dept.${dept}`)}
                    </option>
                  ))}
                </NativeSelect>
              </div>
              <div className="grid gap-1">
                <Label htmlFor="req-open">{t('hr.openings')}</Label>
                <Input id="req-open" type="number" min={1} value={openings} onChange={(e) => setOpenings(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="grid gap-1">
                  <Label htmlFor="req-min">{t('hr.salaryMin')}</Label>
                  <Input id="req-min" value={salaryMin} onChange={(e) => setSalaryMin(e.target.value)} />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="req-max">{t('hr.salaryMax')}</Label>
                  <Input id="req-max" value={salaryMax} onChange={(e) => setSalaryMax(e.target.value)} />
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t('hr.salaryInternalOnly')}</p>
            <div className="flex gap-2">
              <Button
                disabled={jobTitle.trim() === '' || Number(openings) < 1}
                onClick={() => {
                  onError(null);
                  create.mutate(
                    {
                      jobTitle,
                      department: department as never,
                      openings: Number(openings),
                      ...(salaryMin ? { salaryMin } : {}),
                      ...(salaryMax ? { salaryMax } : {}),
                    },
                    {
                      onSuccess: () => setAdding(false),
                      onError: onErrorCb,
                    },
                  );
                }}
              >
                {t('common.save')}
              </Button>
              <Button variant="outline" onClick={() => setAdding(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(requisitions ?? []).map((requisition) => (
          <Card key={requisition.id}>
            <CardContent className="grid gap-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold">{requisition.jobTitle}</h3>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                  {t(`hr.reqStatus.${requisition.status}`)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                <Ltr value={requisition.code} className="font-mono" /> · {t(`hr.dept.${requisition.department}`)} ·{' '}
                {t('hr.openingsCount', { count: requisition.openings })}
              </p>
              {requisition.salaryMin !== null && requisition.salaryMax !== null ? (
                <p className="text-xs">
                  <Amount value={requisition.salaryMin} currency={baseCurrency} /> –{' '}
                  <Amount value={requisition.salaryMax} currency={baseCurrency} />{' '}
                  <span className="text-muted-foreground">({t('hr.internalOnly')})</span>
                </p>
              ) : null}
              <p className="text-xs text-muted-foreground">
                {t('hr.candidatesCount', { count: requisition.candidateCount })} ·{' '}
                {t('hr.hiredCount', { count: requisition.hiredCount })}
              </p>
              <div className="flex flex-wrap gap-2">
                {/* WHICH transition a requisition offers is its status's
                    answer; whether it is usable is the role's. */}
                {requisition.status === 'draft' ? (
                  <Gated permission="hr.edit" reasonKey="perm.cannot.moveRequisitions">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => transition.mutate({ requisitionId: requisition.id, to: 'requested' }, { onError: onErrorCb })}
                    >
                      {t('hr.submitRequisition')}
                    </Button>
                  </Gated>
                ) : null}
                {requisition.status === 'requested' || requisition.status === 'draft' ? (
                  <Gated permission="hr.approve" reasonKey="perm.cannot.approveRequisitions">
                    <Button
                      size="sm"
                      onClick={() => transition.mutate({ requisitionId: requisition.id, to: 'approved' }, { onError: onErrorCb })}
                    >
                      {t('hr.approve')}
                    </Button>
                  </Gated>
                ) : null}
                {requisition.status === 'approved' ? (
                  <Gated permission="hr.approve" reasonKey="perm.cannot.approveRequisitions">
                    <Button
                      size="sm"
                      onClick={() => transition.mutate({ requisitionId: requisition.id, to: 'open' }, { onError: onErrorCb })}
                    >
                      {t('hr.openRequisition')}
                    </Button>
                  </Gated>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Pager
        page={reqPaging.page}
        pages={reqRange.pages}
        first={reqRange.first}
        last={reqRange.last}
        total={reqRange.total}
        onPage={reqPaging.setPage}
        label={t('hr.recTab.requisitions')}
      />
    </div>
  );
}
